import { Router } from '@angular/router';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DataService } from '../../services/data.service';
import { RoleTestingService } from '../../services/role-testing.service';
import { HttpClient } from '@angular/common/http';
import { ConfigService } from '../../services/config.service';

const APPROVER_STATUS_TTL_MS = 365 * 24 * 60 * 60 * 1000;
const IT_APPROVER_TTL_KEY = 'mmaps_it_approver_status_expires_at';
const RE_APPROVER_TTL_KEY = 'mmaps_re_approver_status_expires_at';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: false,
})
export class HeaderComponent implements OnInit {
  userName: string;
  userRoles: string[] = [];
  currentUserEmail: string;
  formId: string | null = null;
  isApproverForForm = false;
  isITFormAccessible = false;
  isJointOwnerCoordinator = false;

  constructor(
    private dataService: DataService,
    private route: ActivatedRoute,
    private router: Router,
    private roleTestingService: RoleTestingService,
    private http: HttpClient,
    private config: ConfigService
  ) {}

  ngOnInit() {
    let userData = JSON.parse(sessionStorage.getItem('userInfo'));
    if (userData) {
      let stringArray = userData.account.name.split(',');
      let lastName = stringArray[0];
      let firstName = stringArray[1].substring(1);
      this.userName = firstName + ' ' + lastName;
      this.currentUserEmail = userData.account.username || userData.preferred_username;

      if (userData.idTokenClaims) {
        // Get user roles with optional role testing override
        const originalRoles = userData.idTokenClaims.roles || [];
        this.userRoles = this.roleTestingService.applyRoleTestingOverride(originalRoles);

        // Check for joint owner coordinator
        this.checkJointOwnerCoordinatorAccess();
      }

      // Mirror app startup dynamic approver scan so first deep-link load
      // can immediately show correct dashboard access in the header.
      this.syncDynamicApproverStatusForHeader(userData);
      // Check for Approver Access, but skip if user is in Real Estate group
      this.route.queryParams.subscribe((params) => {
        this.formId = params['pId'] || null;
        const itGuid = params['guid'] || null;

        if (itGuid) {
          this.checkITFormAccessFromGuid(itGuid);
        }
        // If user already has Real Estate group, skip approver checks
        if (this.userRoles.includes('MMAPS_Real_Estate_Users')) {
          return;
        }
        // If we have a formId, check if user is an approver using existing RRE data
        if (this.formId && this.currentUserEmail) {
          this.checkApproverStatusFromRREData(this.formId);
        } else {
          // Check if user was previously confirmed as Real Estate approver (fallback)
          const savedApproverStatus = sessionStorage.getItem('mmaps_re_approver_status');
          if (savedApproverStatus === 'true') {
            this.isApproverForForm = true;
          }
        }
      });
    } else {
      this.signOut();
    }
  }

  private setApproverStatusWithTTL(type: 'it' | 're') {
    const expiresAt = (Date.now() + APPROVER_STATUS_TTL_MS).toString();
    if (type === 'it') {
      sessionStorage.setItem('mmaps_it_approver_status', 'true');
      localStorage.setItem(IT_APPROVER_TTL_KEY, expiresAt);
      return;
    }
    sessionStorage.setItem('mmaps_re_approver_status', 'true');
    localStorage.setItem(RE_APPROVER_TTL_KEY, expiresAt);
  }

  private syncDynamicApproverStatusForHeader(userData: any) {
    const resolvedEmail = (
      (userData && userData.account && userData.account.username) ||
      (userData && userData.preferred_username) ||
      this.currentUserEmail ||
      ''
    ).toLowerCase();

    if (!resolvedEmail) {
      return;
    }

    const emailAlias = resolvedEmail.includes('@') ? resolvedEmail.split('@')[0] : resolvedEmail;
    const displayName =
      userData && userData.account && userData.account.name
        ? userData.account.name.trim().toLowerCase()
        : '';

    const matchEmail = (raw: any) => {
      if (!raw) return false;
      const norm = String(raw).toLowerCase();
      return norm === resolvedEmail || norm === emailAlias;
    };

    this.dataService.getItSparData({}, 'reviews').subscribe((itRes: any) => {
      const itForms = itRes && itRes.body ? itRes.body : [];
      const isITApprover =
        Array.isArray(itForms) &&
        itForms.some(
          (form: any) =>
            (form.itSponsor && matchEmail(form.itSponsor.mail || form.itSponsor.mailNickname)) ||
            (form.approvers &&
              form.approvers.some((a: any) => matchEmail(a.email || a.mailNickname || a.mail))) ||
            (form.jointOwnerCoordinator && matchEmail(form.jointOwnerCoordinator.mail)) ||
            (form.currentAssignment &&
              matchEmail(form.currentAssignment.mail || form.currentAssignment.mailNickname)) ||
            (form.currentApprover &&
              matchEmail(form.currentApprover.mail || form.currentApprover.mailNickname))
        );

      if (isITApprover) {
        this.setApproverStatusWithTTL('it');
      } else {
        if (sessionStorage.getItem('mmaps_it_approver_status') !== 'true') {
          sessionStorage.removeItem('mmaps_it_approver_status');
        }
      }

      this.dataService.getRREData().subscribe((reRes: any) => {
        const projects = reRes && reRes.projects ? reRes.projects : reRes || [];
        const isREApprover =
          Array.isArray(projects) &&
          projects.some((project: any) => {
            const assignedTo = project.assigned_to;
            const assignedValues = Array.isArray(assignedTo)
              ? assignedTo.map((a: any) => (typeof a === 'string' ? a.trim().toLowerCase() : ''))
              : typeof assignedTo === 'string'
                ? [assignedTo.trim().toLowerCase()]
                : [];
            return (
              assignedValues.includes(resolvedEmail) ||
              (displayName && assignedValues.includes(displayName))
            );
          });

        if (isREApprover) {
          this.setApproverStatusWithTTL('re');
        } else {
          if (sessionStorage.getItem('mmaps_re_approver_status') !== 'true') {
            sessionStorage.removeItem('mmaps_re_approver_status');
          }
        }
      });
    });
  }

  signOut() {
    sessionStorage.setItem('entryURL', window.location.href);
    this.router.navigate(['/login']);
  }

  openProjectSetups() {
    this.router.navigate(['/project-setups']);
  }

  goToDashboard() {
    this.router.navigate(['/dashboard']);
  }

  createNewForm() {
    let currentUser = sessionStorage.getItem('currentUser');
    let userData = sessionStorage.getItem('userInfo');
    let fullTokenPayload = sessionStorage.getItem('fullTokenPayload');
    if (this.hasETSAccess() && !this.hasGasAccess() && !this.hasGenerationAccess()) {
      // Inline check: only standard ETS users and PAC users may create new IT forms.
      const canCreateIT =
        this.userRoles.includes('MMAPS_ETS_Users') ||
        this.userRoles.includes('MMAPS_ETS_PAC_Users');
      if (!canCreateIT) {
        console.warn('User does not have permission to create new IT forms.');
        return;
      }
      sessionStorage.clear();
      sessionStorage.setItem('userInfo', userData);
      sessionStorage.setItem('currentUser', currentUser);
      sessionStorage.setItem('fullTokenPayload', fullTokenPayload);
      this.router.navigate(['/it-project-setup']);
    } else if (this.hasGasAccess() && !this.hasETSAccess() && !this.hasGenerationAccess()) {
      sessionStorage.clear();
      sessionStorage.setItem('userInfo', userData);
      sessionStorage.setItem('currentUser', currentUser);
      sessionStorage.setItem('fullTokenPayload', fullTokenPayload);
      this.router.navigate(['/funding-project']);
    } else if (this.hasGenerationAccess() && !this.hasGasAccess() && !this.hasETSAccess()) {
      // Require Real Estate role to create RRE/Generation forms. Approver-only users (no RE role) cannot create.
      const canCreateRE = this.userRoles.includes('MMAPS_Real_Estate_Users');
      if (!canCreateRE) {
        console.warn('User does not have permission to create new Real Estate forms.');
        return;
      }
      sessionStorage.clear();
      sessionStorage.setItem('userInfo', userData);
      sessionStorage.setItem('currentUser', currentUser);
      sessionStorage.setItem('fullTokenPayload', fullTokenPayload);
      this.router.navigate(['/rre-projects']);
    } else {
      this.goToDashboard();
    }
  }

  formDashboard(dashboardType: string) {
    if (dashboardType === 'IT_SPAR_DASHBOARD') {
      this.router.navigate(['/form-dashboard']);
      return;
    }
    if (dashboardType === 'REAL_ESTATE_DASHBOARD') {
      this.router.navigate(['/real-estate-form-dashboard']);
      return;
    }
  }

  hasETSAccess() {
    // IT dashboard access: role or dynamic IT approver
    return (
      this.userRoles.includes('MMAPS_ETS_Users') ||
      this.userRoles.includes('MMAPS_ETS_Asset_Users') ||
      this.userRoles.includes('MMAPS_ETS_PAC_Users') ||
      this.userRoles.includes('MMAPS_ETS_RO_Users') ||
      this.isJointOwnerCoordinator ||
      this.isITFormAccessible ||
      sessionStorage.getItem('mmaps_it_approver_status') === 'true'
    );
  }

  hasGasAccess() {
    return this.userRoles.includes('MMAPS_Gas_Users');
  }

  hasGenerationAccess() {
    return this.userRoles.includes('MMAPS_Generation_Users');
  }

  hasRealEstateAccess() {
    // Real Estate dashboard access: role or dynamic RE approver
    if (this.userRoles.includes('MMAPS_Real_Estate_Users')) return true;
    if (sessionStorage.getItem('mmaps_re_approver_status') === 'true') return true;
    return this.formId && this.isApproverForForm;
  }

  hasLegacySPARAccess() {
    return (
      this.userRoles.includes('MMAPS_ETS_PAC_Users') || this.userRoles.includes('MMAPS_ETS_Users')
    );
  }

  goToLegacyForm() {
    window.open(this.dataService.getLegacyFormLink());
  }

  // Check if user is the Joint Owner Coordinator
  checkJointOwnerCoordinatorAccess() {
    this.dataService.getJointOwnerCoordinator().subscribe(
      (coordinator: any) => {
        if (coordinator && coordinator.mail && this.currentUserEmail) {
          this.isJointOwnerCoordinator =
            coordinator.mail.toLowerCase() === this.currentUserEmail.toLowerCase();
        }
      },
      (error) => {
        console.error('Error checking joint owner coordinator in header:', error);
        this.isJointOwnerCoordinator = false;
      }
    );
  }

  // Get the count of dashboards the user has access to
  getDashboardAccessCount(): number {
    let count = 0;
    if (this.hasETSAccess()) count++;
    if (this.hasRealEstateAccess()) count++;
    return count;
  }

  // Get the single dashboard type if user has access to only one
  getSingleDashboardType(): string | null {
    const accessCount = this.getDashboardAccessCount();
    if (accessCount !== 1) return null;

    if (this.hasETSAccess()) return 'IT_SPAR_DASHBOARD';
    if (this.hasRealEstateAccess()) return 'REAL_ESTATE_DASHBOARD';
    return null;
  }

  // Navigate directly to single dashboard
  navigateToSingleDashboard() {
    const dashboardType = this.getSingleDashboardType();
    if (dashboardType) {
      this.formDashboard(dashboardType);
    }
  }

  checkITFormAccessFromGuid(guid: string) {
    if (!guid) {
      return;
    }

    const callBody = {
      itSparGuid: guid,
    };

    this.dataService.getItSparData(callBody, 'ui').subscribe(
      (result: any) => {
        if (result && result.body) {
          this.isITFormAccessible = true;
          this.setApproverStatusWithTTL('it');
        }
      },
      () => {
        this.isITFormAccessible = false;
      }
    );
  }

  // Check approver status using individual project endpoint
  checkApproverStatusFromRREData(formId: string) {
    if (!this.currentUserEmail) {
      console.warn('No user email available for approver check (header)');
      return;
    }

    // Use individual project endpoint to get project with approver data
    let userInfo = JSON.parse(sessionStorage.getItem('userInfo'));
    const headers = {
      Authorization: userInfo.idToken,
      'Content-Type': 'application/json',
    };

    this.http
      .get(this.config.getConfig('ApiBaseURLAWS') + `/mmaps/real-estate/project?id=${formId}`, {
        headers,
      })
      .subscribe(
        (result: any) => {
          console.log('Individual project data received (header):', result);
          console.log('Project keys:', Object.keys(result || {}));

          // The actual project data is in result.project
          const projectData = result.project || result;
          console.log('Project data keys:', Object.keys(projectData || {}));

          // Check for various possible approver properties
          const possibleApproverProps = Object.keys(projectData || {}).filter(
            (key) =>
              key.toLowerCase().includes('approv') ||
              key.toLowerCase().includes('review') ||
              key.toLowerCase().includes('user') ||
              key.toLowerCase().includes('assign') ||
              key.toLowerCase().includes('workflow')
          );
          console.log('Possible approver properties in project data:', possibleApproverProps);

          // Log values of these properties
          possibleApproverProps.forEach((prop) => {
            console.log(`Project ${prop}:`, projectData[prop]);
          });

          // Check specific approver properties
          console.log('Project doaApprover:', projectData.doaApprover);
          console.log('Project workflow:', projectData.workflow);

          // Check if current user is an approver
          let isApprover = false;

          // Check doaApprover property
          if (projectData.doaApprover) {
            if (typeof projectData.doaApprover === 'string') {
              isApprover =
                projectData.doaApprover.toLowerCase() ===
                (this.currentUserEmail && this.currentUserEmail.toLowerCase());
              console.log(
                'DoaApprover string check:',
                projectData.doaApprover,
                'vs',
                this.currentUserEmail,
                '=',
                isApprover
              );
            } else if (Array.isArray(projectData.doaApprover)) {
              isApprover = projectData.doaApprover.some((item: any) => {
                // doaApprover items have an 'approver' property with user details
                const approverEmail =
                  (item && item.approver && item.approver.mail) ||
                  (item && item.approver && item.approver.email) ||
                  (item && item.email) ||
                  (typeof item === 'string' ? item : null);
                console.log('Checking doaApprover item:', item, 'extracted email:', approverEmail);
                return (
                  approverEmail &&
                  this.currentUserEmail &&
                  approverEmail.toLowerCase() === this.currentUserEmail.toLowerCase()
                );
              });
              console.log(
                'DoaApprover array check:',
                projectData.doaApprover,
                'result:',
                isApprover
              );
            } else if (typeof projectData.doaApprover === 'object') {
              const approverEmail =
                (projectData.doaApprover && projectData.doaApprover.email) ||
                (projectData.doaApprover && projectData.doaApprover.userEmail);
              isApprover =
                approverEmail &&
                this.currentUserEmail &&
                approverEmail.toLowerCase() === this.currentUserEmail.toLowerCase();
              console.log(
                'DoaApprover object check:',
                projectData.doaApprover,
                'result:',
                isApprover
              );
            }
          }

          // Check workflow property if not found in doaApprover
          if (!isApprover && projectData.workflow) {
            if (Array.isArray(projectData.workflow)) {
              isApprover = projectData.workflow.some((workflowItem: any) => {
                const approverEmail =
                  (workflowItem && workflowItem.email) ||
                  (workflowItem && workflowItem.userEmail) ||
                  (workflowItem && workflowItem.approver && workflowItem.approver.email);
                return (
                  approverEmail &&
                  this.currentUserEmail &&
                  approverEmail.toLowerCase() === this.currentUserEmail.toLowerCase()
                );
              });
              console.log('Workflow array check:', projectData.workflow, 'result:', isApprover);
            } else if (typeof projectData.workflow === 'object') {
              const approverEmail =
                (projectData.workflow && projectData.workflow.email) ||
                (projectData.workflow && projectData.workflow.userEmail) ||
                (projectData.workflow &&
                  projectData.workflow.approver &&
                  projectData.workflow.approver.email);
              isApprover =
                approverEmail &&
                this.currentUserEmail &&
                approverEmail.toLowerCase() === this.currentUserEmail.toLowerCase();
              console.log('Workflow object check:', projectData.workflow, 'result:', isApprover);
            }
          }

          this.isApproverForForm = isApprover;

          if (projectData && (projectData.doaApprover || projectData.workflow)) {
            console.log('Approver check completed (header):', {
              doaApprover: projectData.doaApprover,
              workflow: projectData.workflow,
              currentUser: this.currentUserEmail,
              isApprover: this.isApproverForForm,
            });
            // Store approver status in sessionStorage for dashboard access
            if (this.isApproverForForm) {
              this.setApproverStatusWithTTL('re');
            }
          } else {
            this.isApproverForForm = false;
            console.log('No approver data found in project (header):', {
              hasDoaApprover: !!(projectData && projectData.doaApprover),
              hasWorkflow: !!(projectData && projectData.workflow),
              projectKeys: Object.keys(projectData || {}),
              projectData: projectData,
            });
          }

          console.log('Final approver status (header):', this.isApproverForForm);

          // Force change detection to update UI
          if (this.isApproverForForm) {
            console.log('✅ Header: User confirmed as approver - updating navigation UI');
          }
        },
        (error) => {
          console.error('Error fetching individual project data (header):', error);
          this.isApproverForForm = false;
        }
      );
  }
}
